import React from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FiX, FiShoppingBag, FiAlertCircle, FiCheckCircle } from "react-icons/fi";
import { useCart } from "../../context/CartContext";
import { formatCurrency } from "../../utils/currency";
import { validateCart } from "../../utils/cartValidation";
import QuantitySelector from "../ui/QuantitySelector";

const CartDrawer = ({ open, onClose }) => {
  const { items, removeFromCart, updateQuantity, subtotal, savings } = useCart();
  const validation = validateCart(subtotal);

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 z-50 backdrop-blur-sm"
            onClick={onClose}
          />
          {/* Drawer */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-50 flex flex-col shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="font-bold text-lg text-gray-900">
                Shopping Cart {items.length > 0 && <span className="text-gray-400 font-normal text-base">({items.length} items)</span>}
              </h2>
              <button onClick={onClose} className="btn-ghost p-2" aria-label="Close cart">
                <FiX size={20} />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {items.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full gap-4 text-center">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center">
                    <FiShoppingBag size={36} className="text-gray-400" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-700 text-lg">Your cart is empty</p>
                    <p className="text-gray-400 text-sm mt-1">Add some compact electronics to get started.</p>
                  </div>
                  <Link to="/shop" onClick={onClose} className="btn-primary mt-2">
                    Browse Products
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {items.map((item) => (
                    <div key={item.id} className="flex gap-3 py-3 border-b border-gray-100 last:border-0">
                      <Link to={`/product/${item.slug}`} onClick={onClose}>
                        <img
                          src={item.image}
                          alt={item.title}
                          className="w-20 h-20 object-cover rounded-xl bg-gray-100 flex-shrink-0"
                        />
                      </Link>
                      <div className="flex-1 min-w-0">
                        <Link to={`/product/${item.slug}`} onClick={onClose}>
                          <p className="text-sm font-semibold text-gray-900 leading-tight line-clamp-2 hover:text-brand-600 transition-colors">
                            {item.title}
                          </p>
                        </Link>
                        <p className="text-brand-600 font-bold text-sm mt-1">{formatCurrency(item.price)}</p>
                        <div className="flex items-center justify-between mt-2">
                          <QuantitySelector
                            quantity={item.quantity}
                            onChange={(qty) => updateQuantity(item.id, qty)}
                            max={item.stock}
                          />
                          <div className="text-right">
                            <p className="text-sm font-bold text-gray-900">
                              {formatCurrency(item.price * item.quantity)}
                            </p>
                            <button
                              onClick={() => removeFromCart(item.id)}
                              className="text-xs text-red-500 hover:text-red-700 mt-0.5"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            {items.length > 0 && (
              <div className="border-t border-gray-100 px-6 py-4 space-y-3">
                {/* Validation */}
                <div
                  className={`flex items-start gap-2 p-3 rounded-xl text-sm ${
                    validation.valid ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
                  }`}
                >
                  {validation.valid ? (
                    <FiCheckCircle size={16} className="mt-0.5 flex-shrink-0" />
                  ) : (
                    <FiAlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                  )}
                  <span>
                    {validation.valid ? "Ready to checkout!" : validation.message}
                  </span>
                </div>

                {savings > 0 && (
                  <div className="flex justify-between text-sm text-emerald-600 font-medium">
                    <span>You save</span>
                    <span>−{formatCurrency(savings)}</span>
                  </div>
                )}

                <div className="flex justify-between font-bold text-gray-900 text-lg">
                  <span>Subtotal</span>
                  <span>{formatCurrency(subtotal)}</span>
                </div>

                <div className="flex gap-3">
                  <Link
                    to="/cart"
                    onClick={onClose}
                    className="btn-secondary flex-1 text-center text-sm"
                  >
                    View Cart
                  </Link>
                  <Link
                    to={validation.valid ? "/checkout" : "#"}
                    onClick={(e) => {
                      if (!validation.valid) { e.preventDefault(); return; }
                      onClose();
                    }}
                    className={`btn-primary flex-1 text-center text-sm ${!validation.valid ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    Checkout
                  </Link>
                </div>
                <p className="text-xs text-gray-400 text-center">
                  Free shipping on orders over $99
                </p>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
